import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VendorDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "",
    description: "",
    price: "",
    category: "commerce",
    stock: "",
    image_url: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/products?vendor_id=${user.id}`),
        axios.get(`${API}/orders?vendor_id=${user.id}`)
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/products`, {
        ...newProduct,
        price: parseFloat(newProduct.price),
        stock: parseInt(newProduct.stock),
        vendor_id: user.id
      });
      toast.success("Produit créé avec succès!");
      setShowProductDialog(false);
      setNewProduct({ name: "", description: "", price: "", category: "commerce", stock: "", image_url: "" });
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la création du produit");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Supprimer ce produit?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Produit supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <nav className="bg-white border-b border-border/40 px-4 md:px-8 lg:px-12 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/woma-logo.png"
              alt="WÔMA de ARTEKNOS"
              className="h-12 w-12 rounded-full object-cover ring-2 ring-[#D4AF37]/40 shadow-lg shadow-[#D4AF37]/20"
              data-testid="vendor-logo"
            />
            <h1 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif', color: 'hsl(var(--primary))'}}>
              <span className="text-[#D4AF37]">WÔMA</span> · Espace Vendeur
            </h1>
          </div>
          <div className="flex gap-4 items-center">
            <span className="text-sm text-muted-foreground">Bonjour, {user.full_name}</span>
            <Button onClick={() => navigate('/')} variant="outline" className="rounded-full" data-testid="vendor-nav-home-btn">Accueil</Button>
            <Button onClick={onLogout} variant="outline" className="rounded-full" data-testid="vendor-logout-btn">Déconnexion</Button>
          </div>
        </div>
      </nav>

      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="vendor-dashboard-title">Gestion des produits</h2>
            <p className="text-muted-foreground">Gérez votre catalogue et vos commandes</p>
          </div>

          <div className="mb-8">
            <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90 text-white rounded-full" data-testid="add-product-btn">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter un produit
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nouveau produit</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateProduct} className="space-y-4">
                  <div>
                    <Label htmlFor="product-name">Nom du produit</Label>
                    <Input id="product-name" value={newProduct.name} onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} required data-testid="product-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="product-description">Description</Label>
                    <Input id="product-description" value={newProduct.description} onChange={(e) => setNewProduct({...newProduct, description: e.target.value})} required data-testid="product-description-input" />
                  </div>
                  <div>
                    <Label htmlFor="product-price">Prix (FCFA)</Label>
                    <Input id="product-price" type="number" step="1" value={newProduct.price} onChange={(e) => setNewProduct({...newProduct, price: e.target.value})} required data-testid="product-price-input" />
                  </div>
                  <div>
                    <Label htmlFor="product-category">Catégorie</Label>
                    <select id="product-category" className="h-12 w-full rounded-lg border-input bg-background px-3 py-2 text-sm" value={newProduct.category} onChange={(e) => setNewProduct({...newProduct, category: e.target.value})} data-testid="product-category-select">
                      <option value="commerce">Commerce</option>
                      <option value="boutique">Boutique</option>
                      <option value="tailor">Artisan</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="product-stock">Stock</Label>
                    <Input id="product-stock" type="number" value={newProduct.stock} onChange={(e) => setNewProduct({...newProduct, stock: e.target.value})} required data-testid="product-stock-input" />
                  </div>
                  <div>
                    <Label htmlFor="product-image">URL de l'image (optionnel)</Label>
                    <Input id="product-image" value={newProduct.image_url} onChange={(e) => setNewProduct({...newProduct, image_url: e.target.value})} data-testid="product-image-input" />
                  </div>
                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white rounded-full" data-testid="product-submit-btn">Créer le produit</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="mb-8">
            <CardHeader>
              <CardTitle data-testid="products-section-title">Mes produits</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-center py-8 text-muted-foreground">Chargement...</p>
              ) : products.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground" data-testid="no-products-message">Aucun produit. Ajoutez-en un pour commencer!</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <div key={product.id} className="border border-border/40 rounded-xl p-4 hover:shadow-md transition-all" data-testid={`product-card-${product.id}`}>
                      {product.image_url && <img src={product.image_url} alt={product.name} className="w-full h-32 object-cover rounded-lg mb-3" />}
                      <h3 className="font-semibold mb-1">{product.name}</h3>
                      <p className="text-sm text-muted-foreground mb-2">{product.description}</p>
                      <p className="text-lg font-bold text-primary mb-2">{new Intl.NumberFormat('fr-FR').format(product.price)} FCFA</p>
                      <p className="text-sm text-muted-foreground mb-3">Stock: {product.stock}</p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => handleDeleteProduct(product.id)} data-testid={`delete-product-${product.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle data-testid="vendor-orders-title">Commandes reçues</CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground" data-testid="no-vendor-orders-message">Aucune commande pour le moment</p>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.id} className="border border-border/40 rounded-xl p-4" data-testid={`vendor-order-${order.id}`}>
                      <p className="font-semibold">Commande #{order.id.substring(0, 8)}</p>
                      <p className="text-sm">Client: {order.customer_name}</p>
                      <p className="text-sm">Montant: {new Intl.NumberFormat('fr-FR').format(order.total_amount)} FCFA</p>
                      <p className="text-sm">Statut: {order.status}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
